import React, { useEffect, useState } from "react";
import { 
  Alert, Button, Card, Divider, Form, Input, Space, Tag, Select,
  message, Tabs, Tooltip, Typography, Spin, Breadcrumb, Modal, Upload 
} from "antd";
import { FaCheck, FaUndo, FaArchive, FaArrowLeft, FaEye } from "react-icons/fa";
import { SettingOutlined, ThunderboltOutlined, UploadOutlined } from "@ant-design/icons";
import { useParams, useNavigate, Link } from "react-router-dom";
import axiosHttp from "../../appRedux/services/axiosCommon";
import Page from "../../components/Common/Page";
import AIGeneratorConfig from "../../components/Common/AIGeneratorConfig";

const PAGE_STATUS_COLORS = {
  missing: "default",
  draft: "gold",
  published: "green",
  archived: "red",
};

const STAGE_LEVEL_LABELS = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

const emptyStringArray = (value) => (Array.isArray(value) ? value : []);

const normalizePageFormValues = (values, editingPage = {}) => ({
  ...values,
  seo: {
    ...((values.seo === undefined ? editingPage.seo : values.seo) || {}),
    keywords: emptyStringArray((values.seo === undefined ? editingPage.seo : values.seo)?.keywords).filter(Boolean),
  },
  quickStats: (values.quickStats === undefined ? editingPage.quickStats : values.quickStats) || {},
  overview: {
    ...((values.overview === undefined ? editingPage.overview : values.overview) || {}),
    whereUsed: emptyStringArray((values.overview === undefined ? editingPage.overview : values.overview)?.whereUsed).filter(Boolean),
  },
  whoShouldLearn: emptyStringArray(values.whoShouldLearn === undefined ? editingPage.whoShouldLearn : values.whoShouldLearn).filter(Boolean),
  relatedEcosystems: emptyStringArray(values.relatedEcosystems === undefined ? editingPage.relatedEcosystems : values.relatedEcosystems).filter(Boolean),
  careerOutcomes: emptyStringArray(values.careerOutcomes === undefined ? editingPage.careerOutcomes : values.careerOutcomes).filter((item) => item?.role || item?.description),
  faq: emptyStringArray(values.faq === undefined ? editingPage.faq : values.faq).filter((item) => item?.question || item?.answer),
  skillPairings: emptyStringArray(values.skillPairings === undefined ? editingPage.skillPairings : values.skillPairings).filter((item) => item?.slug || item?.name),
  stages: emptyStringArray(values.stages === undefined ? editingPage.stages : values.stages).map(({ level, id, ...stage }) => stage),
});

const getApiErrorMessage = (error, fallback) => (
  error.response?.data?.error?.message ||
  error.response?.data?.message ||
  error.message ||
  fallback
);

const StringList = ({ name, addLabel, placeholder }) => (
  <Form.List name={name}>
    {(fields, { add, remove }) => (
      <div>
        {fields.map((field) => (
          <Space key={field.key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
            <Form.Item {...field} style={{ flex: 1, marginBottom: 0 }}>
              <Input placeholder={placeholder} />
            </Form.Item>
            <Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>
          </Space>
        ))}
        <Button type="dashed" onClick={() => add()} block>{addLabel}</Button>
      </div>
    )}
  </Form.List>
);

const StatusPill = ({ status }) => (
  <Tag color={PAGE_STATUS_COLORS[status]} style={{ margin: 0, textTransform: "uppercase", fontWeight: 700, fontSize: 10, padding: "0 6px" }}>
    {status || "missing"}
  </Tag>
);

const SkillPageBuilder = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [pageForm] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [topics, setTopics] = useState([]);
  
  const [configVisible, setConfigVisible] = useState(false);
  const [previewPage, setPreviewPage] = useState(null);
  const [generatingSection, setGeneratingSection] = useState({
    overview: false,
    roadmap: false,
    faq: false,
    ecosystem: false,
  });

  const fetchTopics = async () => {
    try {
      const response = await axiosHttp.get("/v3/admin/topics");
      const data = response.data?.data || response.data?.results || response.data || [];
      setTopics(data);
    } catch (error) {
      console.error("Failed to fetch topics for skill pairings");
    }
  };

  useEffect(() => {
    fetchPage();
    fetchTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${pageId}`);
      const page = response.data?.data;
      if (!page) throw new Error("Skill page not found");
      
      setEditingPage(page);
      pageForm.setFieldsValue({
        ...page,
        stages: page.stages || [],
        seo: {
          keywords: [],
          ...(page.seo || {}),
        },
        quickStats: page.quickStats || {},
        overview: {
          whereUsed: [],
          ...(page.overview || {}),
        },
        overview_prompt: "Generate skill applications and curation text.",
        roadmap_prompt: "Generate a 3-stage roadmap (beginner, intermediate, advanced).",
        faq_prompt: "Generate a list of frequently asked questions.",
        ecosystem_prompt: "Generate related ecosystem components, semantic context, and progressions.",
      });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load skill page"));
      navigate("/skill/list?tab=pages"); // Redirect back on error
    } finally {
      setLoading(false);
    }
  };

  const handleOgImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("ogImage", file);
    try {
      const response = await axiosHttp.post(
        `/v3/admin/skill-pages/${pageId}/og-image`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const url = response.data?.data?.ogImage;
      if (url) {
        setEditingPage(prev => ({ ...prev, ogImage: url }));
        message.success("OG image uploaded successfully.");
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to upload OG image"));
    }
  };

  const handlePageSubmit = async () => {
    try {
      const values = await pageForm.validateFields();
      setSaving(true);
      const payload = normalizePageFormValues(values, editingPage);
      const response = await axiosHttp.put(`/v3/admin/skill-pages/${pageId}`, payload);
      const updated = response.data?.data;
      if (!updated) throw new Error("Failed to save skill page");
      setEditingPage(updated);
      pageForm.setFieldsValue(updated);
      message.success("Skill page saved.");
    } catch (error) {
      if (error?.errorFields) return;
      message.error(getApiErrorMessage(error, "Failed to save skill page"));
    } finally {
      setSaving(false);
    }
  };

  const changePageStatus = async (status) => {
    try {
      const response = await axiosHttp.patch(`/v3/admin/skill-pages/${pageId}/status`, { status });
      if (!response.data?.data) throw new Error("Failed to update status");
      message.success(`Skill page moved to ${status}.`);
      setEditingPage({ ...editingPage, status });
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to update page status"));
    }
  };

  const handleGenerateSection = async (section) => {
    setGeneratingSection(prev => ({ ...prev, [section]: true }));
    setTimeout(() => {
      setGeneratingSection(prev => ({ ...prev, [section]: false }));
      message.info({
        content: `AI generation for ${section} is coming soon!`,
        duration: 3
      });
    }, 1500);
  };

  const openPreview = async () => {
    try {
      const response = await axiosHttp.get(`/v3/admin/skill-pages/${pageId}`);
      if (!response.data?.data) throw new Error("Skill page not found");
      setPreviewPage(response.data.data);
    } catch (error) {
      message.error(getApiErrorMessage(error, "Failed to load preview"));
    }
  };

  if (loading && !editingPage) {
    return (
      <Page title="Loading Page Builder...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" tip="Loading Workspace..." />
        </div>
      </Page>
    );
  }

  return (
    <Page title={editingPage ? `Builder: ${editingPage.name}` : "Page Builder"}>
      <div className="page-wrapper" style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: 60 }}>
        
        {/* Workspace Sticky Header */}
        <div style={{ 
          position: 'sticky', 
          top: 60, 
          zIndex: 100, 
          background: '#fff', 
          padding: '16px 24px', 
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24
        }}>
          <div>
            <Breadcrumb style={{ marginBottom: 4 }}>
              <Breadcrumb.Item>
                <Link to="/skill/list?tab=pages" style={{ color: '#64748b' }}>
                  <FaArrowLeft style={{ marginRight: 6, fontSize: 12 }} /> 
                  Topics & Skills
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Page Builder</Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>
                {editingPage?.name || "Untitled Page"}
              </h2>
              <StatusPill status={editingPage?.status || "draft"} />
              <div style={{ fontSize: 13, color: "#94a3b8", fontFamily: "monospace" }}>
                /{editingPage?.slug || "draft-slug"}
              </div>
            </div>
          </div>

          <Space size="middle">
            {editingPage?.status === "draft" && (
              <Button onClick={() => changePageStatus("published")} icon={<FaCheck style={{ color: '#52c41a' }}/>}>
                Publish
              </Button>
            )}
            {editingPage?.status === "published" && (
              <Button onClick={() => changePageStatus("draft")} icon={<FaUndo style={{ color: '#faad14' }}/>}>
                Revert to Draft
              </Button>
            )}
            {editingPage?.status === "archived" ? (
              <Button onClick={() => changePageStatus("draft")} icon={<FaUndo />}>
                Restore
              </Button>
            ) : (
              <Button onClick={() => changePageStatus("archived")} danger icon={<FaArchive />}>
                Archive
              </Button>
            )}

            <div style={{ width: 1, height: 24, background: '#e2e8f0', margin: '0 8px' }} />

            <Tooltip title="Configure AI Models & Prompts globally">
              <Button icon={<SettingOutlined />} onClick={() => setConfigVisible(true)}>
                AI Settings
              </Button>
            </Tooltip>
            
            <Button icon={<FaEye />} onClick={openPreview}>
              Preview
            </Button>
            
            <Button type="primary" loading={saving} onClick={handlePageSubmit} style={{ minWidth: 120 }}>
              Save Changes
            </Button>
          </Space>
        </div>

        {/* Workspace Body */}
        <div className="container-fluid" style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Form layout="vertical" form={pageForm}>
            <Card style={{ borderRadius: 12, borderColor: "#e2e8f0", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }} bodyStyle={{ padding: '8px 24px 24px' }}>
              <Tabs defaultActiveKey="1" size="large" tabBarGutter={32}>
                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Basic Information</span>} key="1">
                  <div style={{ paddingTop: 16 }}>
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Item label="Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
                          <Input size="large" />
                        </Form.Item>
                      </div>
                      <div className="col-md-6">
                        <Form.Item label="Slug" name="slug" rules={[{ required: true, message: "Slug is required" }]}>
                          <Input size="large" disabled={editingPage?.status !== "draft"} />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-3"><Form.Item label="Icon" name="icon"><Input /></Form.Item></div>
                      <div className="col-md-3"><Form.Item label="Color" name="color"><Input placeholder="#14b8a6" /></Form.Item></div>
                      <div className="col-md-6"><Form.Item label="Demand Signal" name="demandSignal"><Input /></Form.Item></div>
                    </div>

                    <Divider orientation="left" style={{ marginTop: 32 }}>Quick Stats</Divider>
                    <div className="row">
                      <div className="col-md-3"><Form.Item label="Paths" name={["quickStats", "paths"]}><Input type="number" /></Form.Item></div>
                      <div className="col-md-3"><Form.Item label="Courses" name={["quickStats", "courses"]}><Input type="number" /></Form.Item></div>
                      <div className="col-md-3"><Form.Item label="Salary Range" name={["quickStats", "salaryRange"]}><Input /></Form.Item></div>
                      <div className="col-md-3"><Form.Item label="Level" name={["quickStats", "level"]}><Input /></Form.Item></div>
                    </div>
                  </div>
                </Tabs.TabPane>

                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>SEO & Hero (Manual)</span>} key="2" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <div className="row">
                      <div className="col-md-6">
                        <Form.Item label="Primary Keyword" name={["seo", "primaryKeyword"]} rules={[{ required: true, message: "Primary keyword is required" }]}>
                          <Input size="large" />
                        </Form.Item>
                      </div>
                      <div className="col-md-6">
                        <Form.Item label="Title Suffix" name={["seo", "titleSuffix"]}>
                          <Input size="large" />
                        </Form.Item>
                      </div>
                    </div>
                    <Form.Item label="Short Description (Meta)" name={["seo", "shortDescription"]} rules={[{ required: true, message: "Short description is required" }]}>
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item label="Keywords">
                      <StringList name={["seo", "keywords"]} addLabel="Add Keyword" placeholder="Keyword" />
                    </Form.Item>

                    <Divider orientation="left" style={{ marginTop: 32 }}>Hero Section & Overview</Divider>
                    <Form.Item label="Subtitle" name="subtitle"><Input size="large" /></Form.Item>
                    <Form.Item label="Hero Description" name={["overview", "description"]}><Input.TextArea rows={3} /></Form.Item>
                    <Form.Item label="Why Relevant" name={["overview", "whyRelevant"]}><Input.TextArea rows={3} /></Form.Item>
                    <Form.Item label="Industry Demand" name={["overview", "industryDemand"]}><Input.TextArea rows={2} /></Form.Item>

                    <Divider orientation="left" style={{ marginTop: 32 }}>OG Image (Social Preview)</Divider>
                    {editingPage?.ogImage && (
                      <div style={{ marginBottom: 16 }}>
                        <img
                          src={editingPage.ogImage}
                          alt="OG Preview"
                          style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid #e2e8f0" }}
                        />
                      </div>
                    )}
                    <Upload
                      accept="image/jpeg,image/png,image/webp"
                      maxCount={1}
                      beforeUpload={(file) => {
                        handleOgImageUpload(file);
                        return false;
                      }}
                      showUploadList={false}
                    >
                      <Button icon={<UploadOutlined />}>
                        {editingPage?.ogImage ? "Replace OG Image" : "Upload OG Image"}
                      </Button>
                    </Upload>
                  </div>
                </Tabs.TabPane>

                <Tabs.TabPane tab={<span style={{ fontWeight: 600 }}>Careers (Manual)</span>} key="3" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Form.Item label="Who Should Learn">
                      <StringList name="whoShouldLearn" addLabel="Add Audience" placeholder="Audience" />
                    </Form.Item>

                    <Divider orientation="left" style={{ marginTop: 32 }}>Career Metadata</Divider>
                    <div className="row">
                      <div className="col-md-4"><Form.Item label="Salary Range" name="salaryRange"><Input size="large" /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Industry Trend" name="industryTrend"><Input size="large" /></Form.Item></div>
                      <div className="col-md-4"><Form.Item label="Learning Time Estimate" name="learningTimeEstimate"><Input size="large" /></Form.Item></div>
                    </div>

                    <Divider orientation="left" style={{ marginTop: 32 }}>Career Outcomes Grid</Divider>
                    <Form.List name="careerOutcomes">
                      {(fields, { add, remove }) => (
                        <div>
                          {fields.map((field) => (
                            <Card key={field.key} size="small" style={{ marginBottom: 16, borderRadius: 8, background: '#f8fafc' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                              <div className="row">
                                <div className="col-md-6"><Form.Item label="Role" name={[field.name, "role"]}><Input /></Form.Item></div>
                                <div className="col-md-3"><Form.Item label="Trend" name={[field.name, "trend"]}><Input /></Form.Item></div>
                                <div className="col-md-3"><Form.Item label="Salary" name={[field.name, "salary"]}><Input /></Form.Item></div>
                              </div>
                              <Form.Item label="Description" name={[field.name, "description"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} /></Form.Item>
                            </Card>
                          ))}
                          <Button type="dashed" size="large" block onClick={() => add()}>Add Career Outcome</Button>
                        </div>
                      )}
                    </Form.List>
                  </div>
                </Tabs.TabPane>

                <Tabs.TabPane tab={<span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#3B82F6' }}><ThunderboltOutlined /> AI Content Engine</span>} key="4" forceRender>
                  <div style={{ paddingTop: 16 }}>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 24, border: '1px solid #bae6fd', background: '#f0f9ff' }}
                      message="These sections contain dense content that can be fully generated by AI. Modify prompts individually to customize the generator output."
                    />

                    {/* SECTION 1: Overview & Applications */}
                    <Card 
                      size="small" 
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Overview & Applications</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                    >
                      <div style={{ marginBottom: 20, display: "flex", gap: 16, alignItems: "center", background: "#f1f5f9", padding: "16px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item name="overview_prompt" noStyle>
                            <Input 
                              size="large"
                              placeholder="Custom AI instructions for generating this section..." 
                              disabled={generatingSection.overview}
                            />
                          </Form.Item>
                        </div>
                        <Button 
                          type="primary"
                          size="large"
                          icon={<ThunderboltOutlined />}
                          loading={generatingSection.overview}
                          onClick={() => handleGenerateSection("overview")}
                          style={{
                            background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                            border: 'none',
                            borderRadius: 8,
                            padding: '0 32px'
                          }}
                        >
                          Generate Overview
                        </Button>
                      </div>
                      <Form.Item label="Where Used">
                        <StringList name={["overview", "whereUsed"]} addLabel="Add Use Case" placeholder="Use case" />
                      </Form.Item>
                      <Form.Item label="Curation Text" name="curationText"><Input.TextArea rows={2} /></Form.Item>
                    </Card>

                    {/* SECTION 2: Roadmap Stages */}
                    <Card 
                      size="small" 
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Roadmap Stages</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                    >
                      <div style={{ marginBottom: 20, display: "flex", gap: 16, alignItems: "center", background: "#f1f5f9", padding: "16px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item name="roadmap_prompt" noStyle>
                            <Input 
                              size="large"
                              placeholder="Custom AI instructions for generating the roadmap..." 
                              disabled={generatingSection.roadmap}
                            />
                          </Form.Item>
                        </div>
                        <Button 
                          type="primary"
                          size="large"
                          icon={<ThunderboltOutlined />}
                          loading={generatingSection.roadmap}
                          onClick={() => handleGenerateSection("roadmap")}
                          style={{
                            background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                            border: 'none',
                            borderRadius: 8,
                            padding: '0 32px'
                          }}
                        >
                          Generate Roadmap
                        </Button>
                      </div>
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Roadmap stage levels are fixed (Beginner, Intermediate, Advanced)."
                      />
                      {(editingPage?.stages || []).map((stage, index) => (
                        <Card
                          key={stage.level}
                          size="small"
                          title={`${STAGE_LEVEL_LABELS[stage.level]} Stage`}
                          style={{ marginBottom: 16, borderRadius: 8, borderColor: "#f1f5f9", background: '#fafafa' }}
                          extra={<Tag color="blue" style={{ margin: 0 }}>{stage.level}</Tag>}
                        >
                          <div className="row">
                            <div className="col-md-4"><Form.Item label="Title" name={["stages", index, "title"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Subtitle" name={["stages", index, "subtitle"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Roadmap Step" name={["stages", index, "roadmapStep"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Description" name={["stages", index, "description"]}><Input.TextArea rows={2} /></Form.Item>
                          <div className="row">
                            <div className="col-md-4"><Form.Item label="Learning Objective" name={["stages", index, "learningObjective"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Expected Outcome" name={["stages", index, "expectedOutcome"]}><Input /></Form.Item></div>
                            <div className="col-md-4"><Form.Item label="Estimated Duration" name={["stages", index, "estimatedDuration"]}><Input /></Form.Item></div>
                          </div>
                          <Form.Item label="Learning Intent" name={["stages", index, "learningIntent"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} /></Form.Item>
                        </Card>
                      ))}
                    </Card>

                    {/* SECTION 3: FAQ */}
                    <Card 
                      size="small" 
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>FAQ</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                    >
                      <div style={{ marginBottom: 20, display: "flex", gap: 16, alignItems: "center", background: "#f1f5f9", padding: "16px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item name="faq_prompt" noStyle>
                            <Input 
                              size="large"
                              placeholder="Custom AI instructions for generating FAQs..." 
                              disabled={generatingSection.faq}
                            />
                          </Form.Item>
                        </div>
                        <Button 
                          type="primary"
                          size="large"
                          icon={<ThunderboltOutlined />}
                          loading={generatingSection.faq}
                          onClick={() => handleGenerateSection("faq")}
                          style={{
                            background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                            border: 'none',
                            borderRadius: 8,
                            padding: '0 32px'
                          }}
                        >
                          Generate FAQs
                        </Button>
                      </div>
                      <Form.List name="faq">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <Form.Item label="Question" name={[field.name, "question"]}><Input /></Form.Item>
                                <Form.Item label="Answer" name={[field.name, "answer"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} /></Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add FAQ</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>

                    {/* SECTION 4: Ecosystem & Related Skills */}
                    <Card 
                      size="small" 
                      title={<span style={{ fontWeight: 700, fontSize: 16 }}>Ecosystem & Related Skills</span>}
                      style={{ marginBottom: 24, borderRadius: 10, borderColor: "#e2e8f0" }}
                    >
                      <div style={{ marginBottom: 20, display: "flex", gap: 16, alignItems: "center", background: "#f1f5f9", padding: "16px", borderRadius: "8px", border: "1px dashed #cbd5e1" }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item name="ecosystem_prompt" noStyle>
                            <Input 
                              size="large"
                              placeholder="Custom AI instructions for generating the ecosystem..." 
                              disabled={generatingSection.ecosystem}
                            />
                          </Form.Item>
                        </div>
                        <Button 
                          type="primary"
                          size="large"
                          icon={<ThunderboltOutlined />}
                          loading={generatingSection.ecosystem}
                          onClick={() => handleGenerateSection("ecosystem")}
                          style={{
                            background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                            border: 'none',
                            borderRadius: 8,
                            padding: '0 32px'
                          }}
                        >
                          Generate Ecosystem
                        </Button>
                      </div>
                      <Form.Item label="Related Ecosystems">
                        <StringList name="relatedEcosystems" addLabel="Add Related Slug" placeholder="skill-slug" />
                      </Form.Item>
                      <Form.Item label="Semantic Context" name="semanticContext"><Input.TextArea rows={3} /></Form.Item>
                      <Form.Item label="Career Progression Note" name="careerProgressionNote"><Input.TextArea rows={3} /></Form.Item>
                      <Form.List name="skillPairings">
                        {(fields, { add, remove }) => (
                          <div>
                            {fields.map((field) => (
                              <Card key={field.key} size="small" style={{ marginBottom: 12, borderRadius: 8, background: '#fafafa' }} extra={<Button danger type="text" onClick={() => remove(field.name)}>Remove</Button>}>
                                <div className="row">
                                  <div className="col-md-6">
                                    <Form.Item label="Topic (Search & Select)" name={[field.name, "slug"]}>
                                      <Select
                                        showSearch
                                        placeholder="Search topic..."
                                        optionFilterProp="label"
                                        options={topics.map(t => ({
                                          label: `${t.name} ${t.page?.slug ? `(/${t.page.slug})` : ''}`,
                                          value: t.page?.slug || t.slug || t.code,
                                          topicName: t.name
                                        }))}
                                        onChange={(val, option) => {
                                          const pairings = pageForm.getFieldValue('skillPairings') || [];
                                          pairings[field.name] = {
                                            ...pairings[field.name],
                                            slug: option.value,
                                            name: option.topicName
                                          };
                                          pageForm.setFieldsValue({ skillPairings: pairings });
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                  <div className="col-md-6"><Form.Item label="Name" name={[field.name, "name"]}><Input /></Form.Item></div>
                                </div>
                                <Form.Item label="Relationship Context" name={[field.name, "relationshipContext"]} style={{ marginBottom: 0 }}><Input.TextArea rows={2} /></Form.Item>
                              </Card>
                            ))}
                            <Button type="dashed" size="large" block onClick={() => add()}>Add Skill Pairing</Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </Card>
          </Form>
        </div>

        {/* Modals & Config */}
        <Modal
          open={!!previewPage}
          onCancel={() => setPreviewPage(null)}
          footer={<Button size="large" onClick={() => setPreviewPage(null)}>Close Preview</Button>}
          width={860}
          title={previewPage ? `Preview: ${previewPage.name}` : "Preview"}
        >
          {previewPage && (
            <div style={{ padding: '8px 0' }}>
              <Space style={{ marginBottom: 16 }}>
                <StatusPill status={previewPage.status} />
                <Tag style={{ margin: 0 }}>/{previewPage.slug}</Tag>
              </Space>
              <Typography.Title level={2} style={{ marginTop: 0 }}>{previewPage.name}</Typography.Title>
              <Typography.Paragraph style={{ fontSize: 16, color: '#475569' }}>{previewPage.subtitle}</Typography.Paragraph>
              <Typography.Paragraph>{previewPage.overview?.description}</Typography.Paragraph>
              <Divider />
              <Typography.Title level={4}>Roadmap</Typography.Title>
              {(previewPage.stages || []).map((stage) => (
                <Card key={stage.level} size="small" style={{ marginBottom: 12, background: '#f8fafc', borderRadius: 8 }}>
                  <Tag color="blue">{stage.level}</Tag>
                  <strong style={{ marginLeft: 8 }}>{stage.title}</strong>
                  <div style={{ marginTop: 8 }}>{stage.description}</div>
                </Card>
              ))}
              <Divider />
              <Typography.Title level={4}>FAQ</Typography.Title>
              {(previewPage.faq || []).map((item, index) => (
                <div key={index} style={{ marginBottom: 16 }}>
                  <strong style={{ fontSize: 15 }}>{item.question}</strong>
                  <div style={{ marginTop: 4, color: '#475569' }}>{item.answer}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>

        <AIGeneratorConfig 
          visible={configVisible} 
          onClose={() => setConfigVisible(false)} 
          category="Skill Page Builder"
          defaultPrompt="You are an expert curriculum designer and career advisor. Generate high quality skill details."
          contextInstructions="Generate structured page content based on skill context."
        />
      </div>
    </Page>
  );
};

export default SkillPageBuilder;
